import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import * as unifiedAlertsService from 'service/unifiedAlerts';
import type { SortModel } from 'components/ColumnFilters/types';
import { TimeRangeValue } from '../AdvancedFilterDrawer/constants';
import { timeRangeToDates, transformTrendData } from '../unifiedAlertsUtils';
import { GenericOption } from 'components/Inputs/MultiSelectAutocomplete';
import { ITSM_TILES_CONFIG } from '../constants';
import { loadDashboardFilters, saveDashboardFilters } from 'service/service';
import {
  ColumnFilterPayloadItem,
  getSortingFromSortModel,
  loadColumnFilterPayload,
  saveColumnFilterPayload,
} from '../columnFilterUtils';

export interface FilterStateOps {
  timeRange: TimeRangeValue;
  startDate?: string;
  endDate?: string;
  incident_state?: GenericOption[];
  priority?: GenericOption[];
  made_sla?: any[];
  response_slaHasBreached? : any[];
  resolution_slaHasBreached?: any[];
  isDandAIAG?: GenericOption[];
  known_error?: GenericOption[];
  u_source?: GenericOption[];
  assignment_group?: GenericOption[];
  department? :GenericOption[];
  capability? : GenericOption[];
  severity? : GenericOption[];
  assignmentGroupListName?: GenericOption[];
  service_offering?: GenericOption[];
  businessUnit?: GenericOption[];
  cmdb_ci?: GenericOption[];
  domain?: GenericOption[];
  program?: GenericOption[];
  vendor? : GenericOption[];
  team? : GenericOption[];
  subCapability? : GenericOption[];
  sector?: GenericOption[];
  technology? : GenericOption[];
  type?: GenericOption[];
  close_code?: GenericOption[];
  problem_state?: GenericOption[];
  service_state?: GenericOption[];
  change_state?: GenericOption[];
  state?: GenericOption[];
  urgency?: GenericOption[];
  contact_type?: GenericOption[];
  miscellaneousSector?: GenericOption[];
  additionalFilters?: Array<{ id: string; field: string; value: string }>;
}

export const FILTER_RELATIONSHIP_FIELD_MAP = {
  department: 'Department',
  capability: 'Capability',
  sector: 'Sector',
  domain: 'Domain',
  program: 'Program',
  businessUnit: 'Business Unit',
  assignment_group: 'Assignment Group',
  assignmentGroupListName: 'Assignment Group List',
  vendor: 'Vendor',
  team: 'Team',
  subCapability: 'Sub_Track',
  technology: 'Technology',
} as const; // severity, status, priority, sources, cmdb_ci not there here because fullMap doesn't contain those relationships.

type DomainInput = string | string[] | null | undefined;

type DateParams = { startDate?: string; endDate?: string };

export interface FilterOptionsOps {
  incident_state?: string[];
  priority?: string[];
  problem_state?: string[];
  service_state?: string[];
  change_state?: string[];
  made_sla?: string[];
  isDandAIAG?: string[];
  known_error?: string[];
  u_source?: string[];
  assignment_group?: string[];
  service_offering?: string[];
  businessUnit?: string[];
  cmdb_ci?: string[];
  domain?: string[];
  program?: string[];
  sector?: string[];
  type?: string[];
  close_code?: string[];
  sectorDropdownValues?: Record<string, string[]>;
  serviceOfferingMap?: Record<string, string[]>;
  assignmentGroupMap?: Record<string, string[]>;
}

type BlockRule = {
  liveTile: string;
  conditions: Record<string, string | boolean | string[]>;
};

export const DEFAULT_FILTERS: FilterStateOps = {
  timeRange: 'last_15_minutes',
  incident_state: ['New', 'In Queue', 'Assigned', 'In Progress', 'Pending', 'Resolved'].map(s => ({ label: s, value: s })),
  problem_state: ["New", "Assess", "Root Cause Analysis", "Fix in Progress"].map(s => ({ label: s, value: s })),
  service_state: ["Open", "Pending Approval", "Work in Progress", "Escalated"].map(s => ({ label: s, value: s })),
  change_state: ["Assess", "Authorize", "Scheduled", "Implement", "Review", "New"].map(s => ({ label: s, value: s })),
  additionalFilters: []
};

const DEFAULT_FILTER_OPTIONS = {
  incident_state: ['New', 'In Queue', 'Assigned', 'In Progress', 'Pending', 'Resolved', 'Canceled', 'Closed'],
  priority: ["P5", "P4", "P3"],
  problem_state: [ "New", "Assess", "Root Cause Analysis", "Fix in Progress", "Closed"],
  service_state: ["Open", "Pending Approval", "Work in Progress", "Escalated", "Closed Complete", "Closed Incomplete", "Closed Rejected"],
  change_state: ["Assess", "Authorize", "Scheduled", "Implement", "Review", "New", "Closed", "Canceled"],
  made_sla: ["True", "False"],
  isDandAIAG: ["Yes", "No"],
  known_error: ["True", "False"],
  u_source: ["Reactive", "Proactive", "Disaster Recovery Gap", "Controls Gap", "Security"],
  type: ["Standard", "Normal", "Emergency", "Expedite"],
  close_code: ["Successful", "Unsuccessful"],
  urgency: ["1 - Critical", "2 - High", "3 - Medium", "4 - Low"],
  contact_type: ["Email", "Phone", "Chat", "System Generated", "Self-Service","Walk-In","Web Chat","Self-Service Platform","Self-Service Mobile","Walk-up","Virtual Agent"],
};

const ITSMTypes = {
  Incidents: 'incident',
  'Problem Tickets': 'problem',
  'Change Requests': 'change_request',
  'Service Requests': 'service_request',
};


const DASHBOARD_FILTER_CONFIG: Record<string, { dashboardId: string; dashboardName: string; pageName: string }> = {
  Incidents: {
    dashboardId: 'incident_board',
    dashboardName: 'Incidents Board',
    pageName: 'Incidents',
  },
  'Service Requests': {
    dashboardId: 'service_request_board',
    dashboardName: 'Service Request Board',
    pageName: 'Service Requests',
  },
  'Problem Tickets': {
    dashboardId: 'problem_ticket_board',
    dashboardName: 'Problem Ticket Board',
    pageName: 'Problem Tickets',
  },
  'Change Requests': {
    dashboardId: 'change_request_board',
    dashboardName: 'Change Request Board',
    pageName: 'Change Requests',
  },
};

const getDashboardFilterConfig = (parentTile: string) => {
  return DASHBOARD_FILTER_CONFIG[parentTile] || DASHBOARD_FILTER_CONFIG.Incidents;
};


const aggregateSummary = (summary: any, keys: string[]) => {
  if (!keys.some(key => summary?.[key])) return null;

  const current = keys.reduce(
    (sum, key) => sum + (summary[key]?.current || 0),
    0
  );

  const previous = keys.reduce(
    (sum, key) => sum + (summary[key]?.previous || 0),
    0
  );

  const difference = keys.reduce(
    (sum, key) => sum + (summary[key]?.difference || 0),
    0
  );

  return {
    current,
    previous,
    difference,
    trend:
      difference > 0
        ? "INCREASED"
        : difference < 0
          ? "DECREASED"
          : "NO_CHANGE",
  };
};

const BLOCK_API_RULES: BlockRule[] = [
  {
    liveTile: 'P3',
    conditions: {
      priority: '3 - Moderate',
    },
  },
  {
    liveTile: 'P4',
    conditions: {
      priority: '4 - Standard',
    },
  },
  {
    liveTile: 'P5',
    conditions: {
      priority: '5 – Regular',
    },
  },
  {
    liveTile: 'Closed Incidents',
    conditions: {
      incident_state: 'Closed',
    },
  },
  {
    liveTile: 'SLA Response Miss',
    conditions: {
      response_slaHasBreached: 'true',
    },
  },
  {
    liveTile: 'SLA Resolution Miss',
    conditions: {
      resolution_slaHasBreached: 'true',
    },
  },
  {
    liveTile: 'Open',
    conditions: {
      problem_state: ['Root Cause Analysis', 'Assess', 'Fix in Progress', 'New'],
    },
  },
  {
    liveTile: 'Open',
    conditions: {
      service_state: 'Open',
    },
  },
  {
    liveTile: 'In Progress',
    conditions: {
      change_state: ['Assess', 'Authorize', 'Scheduled', 'Implement', 'Review'],
    },
  },
  {
    liveTile: 'Closed',
    conditions: {
      change_state: 'Closed',
    },
  },
  {
    liveTile: 'Not Closed',
    conditions: {
      change_state: ["New", "Assess", "Authorize", "Scheduled", "Implement", "Review"],
    },
  },
  {
    liveTile: 'In Progress',
    conditions: {
      service_state: 'Work in Progress',
    },
  },
  {
    liveTile: 'Pending',
    conditions: {
      service_state: 'Pending Approval',
    },
  },
  {
    liveTile: 'Closed Complete',
    conditions: {
      service_state: 'Closed Complete',
    },
  },
  {
    liveTile: 'Closed Incomplete',
    conditions: {
      service_state: 'Closed Incomplete',
    },
  },
  {
    liveTile: 'Known Errors',
    conditions: {
      known_error: 'true',
    },
  },
  {
    liveTile: 'Reactive',
    conditions: {
      u_source: 'Reactive',
    },
  },
  {
    liveTile: 'Made SLA',
    conditions: {
      made_sla: true,
    },
  },
  {
    liveTile: 'SLA Met',
    conditions: {
      made_sla: true,
    },
  },
  {
    liveTile: 'SLA Missed',
    conditions: {
      made_sla: false,
    },
  },
  {
    liveTile: 'RCA',
    conditions: {
      problem_state: "Root Cause Analysis",
    },
  },
  {
    liveTile: 'Emergency',
    conditions: {
      type: 'Emergency',
    },
  },
  {
    liveTile: 'Successful',
    conditions: {
      close_code: 'Successful',
    },
  },
  {
    liveTile: 'Unsuccessful',
    conditions: {
      close_code: 'Unsuccessful',
    },
  },
];

const isEqualArray = (a: any[], b: any[]) => {
  if (a.length !== b.length) return false;

  return a.every((val) => b.includes(val));
};

const shouldBlockApiCall = (filters: Record<string, any>, liveTile: string) => {
  return BLOCK_API_RULES.some((rule) => {
    if (rule.liveTile !== liveTile) return false;

    const conditionKeys = Object.keys(rule.conditions || {});
    const filterKeys = Object.keys(filters || {});

    if (filterKeys.length !== conditionKeys.length) {
      return false;
    }

    return conditionKeys.every((field) => {
      const ruleValue = rule.conditions[field];
      const actualValue = filters[field];

      if (actualValue === undefined || actualValue === null) {
        return false;
      }

      if (Array.isArray(actualValue) && Array.isArray(ruleValue)) {
        return isEqualArray(actualValue, ruleValue);
      }

      if (Array.isArray(actualValue)) {
        return actualValue.some((item) => {
          // handle operator object
          if (typeof item === 'object' && item !== null && 'value' in item) {
            return item.value === ruleValue;
          }

          // normal primitive
          return item === ruleValue;
        });
      }

      return actualValue === ruleValue;
    });
  });
};

export const getSelectedFilterValues = (options?: GenericOption[]) => {
  return (options || [])
    .map(option => option?.value)
    .filter(value => value !== undefined && value !== null && value !== '')
    .map(String);
};

export const getCascadingFilterOptions = (
  currentFilters: FilterStateOps,
  records: any[],
  miscellaneousSectors: string[] = [],
  options?: {
    ignoreDerivedAssignmentGroup?: boolean;
  }
) => {
  const result: Record<string, string[]> = {};

  const filterKeys = Object.keys(
    FILTER_RELATIONSHIP_FIELD_MAP
  ) as Array<keyof typeof FILTER_RELATIONSHIP_FIELD_MAP>;

  /*
   * Check whether at least one relationship-based filter
   * has been selected.
   *
   * If nothing is selected, return an empty result so that
   * the caller continues using the original dropdown options.
   */
  const hasRelationshipFilters = filterKeys.some(key => {
      if (
        key === 'assignment_group' &&
        options?.ignoreDerivedAssignmentGroup
      ) {
        return false;
      }

      const selectedValues =
        currentFilters[key] as GenericOption[] | undefined;

      return getSelectedFilterValues(selectedValues).length > 0;
    });

  // Nothing selected → don't override original dropdown options
  if (!hasRelationshipFilters) {
    return result;
  }

  filterKeys.forEach(currentFilterKey => {
    /*
     * When calculating the options for the current field,
     * ignore that field's own selection.
     *
     * Example:
     *
     * Department = IT
     * Sector = Miscellaneous
     *
     * When calculating Sector options, Sector itself is ignored
     * and Department = IT is used to determine available sectors.
     */

    const otherFilterKeys = filterKeys.filter(key => {
        if (
          key === 'assignment_group' &&
          options?.ignoreDerivedAssignmentGroup
        ) {
          return false;
        }

        return key !== currentFilterKey;
      });

    /*
     * Find relationship records that satisfy all OTHER selected
     * filters.
     *
     * Across different fields → AND
     * Within the same field → OR
     */
    const matchingRecords = records.filter(record => {
      return otherFilterKeys.every(filterKey => {
        const selectedValues = getSelectedFilterValues(
          currentFilters[filterKey] as GenericOption[]
        );

        // Nothing selected in this filter →
        // it should not restrict the result.
        if (selectedValues.length === 0) {
          return true;
        }

        const recordField =
          FILTER_RELATIONSHIP_FIELD_MAP[filterKey];

        const recordValue = record?.[recordField];

        if (
          recordValue === undefined ||
          recordValue === null ||
          recordValue === ''
        ) {
          return false;
        }

        /*
         * Normalize the relationship record value into an array.
         *
         * This allows the same logic to handle:
         *
         * Sector: "Technology"
         *
         * and:
         *
         * Sector: ["Technology", "Cloud"]
         */
        const recordValues = Array.isArray(recordValue)
          ? recordValue.map(String)
          : [String(recordValue)];

        /*
         * Check whether the current relationship record matches
         * ANY selected value in this filter.
         *
         * This gives OR semantics within a filter.
         */
        return selectedValues.some(selectedValue => {
          /*
           * SPECIAL CASE:
           *
           * "Miscellaneous" is a synthetic Sector value.
           *
           * The relationship records do NOT contain:
           *
           *     Sector = "Miscellaneous"
           *
           * Instead they contain the actual miscellaneous sectors:
           *
           *     Russia
           *     AMESA APAC
           *     CTO
           *     DPS
           *     ...
           *
           * Therefore:
           *
           *     Sector = Miscellaneous
           *
           * means:
           *
           *     Sector IN miscellaneousSectors
           */
          if (
            filterKey === 'sector' &&
            selectedValue === 'Miscellaneous'
          ) {
            return recordValues.some(value =>
              miscellaneousSectors.includes(value)
            );
          }

          // Normal relationship matching
          return recordValues.includes(selectedValue);
        });
      });
    });

    /*
     * Now collect the possible values for the field
     * we are currently calculating.
     */
    const targetField =
      FILTER_RELATIONSHIP_FIELD_MAP[currentFilterKey];

    const values = new Set<string>();

    matchingRecords.forEach(record => {
      const value = record?.[targetField];

      const recordValues = Array.isArray(value)
        ? value
        : [value];

      recordValues.forEach(item => {
        if (
          item === null ||
          item === undefined ||
          item === ''
        ) {
          return;
        }

        const stringValue = String(item);

        /*
         * SPECIAL CASE:
         *
         * The relationship records contain the real miscellaneous
         * sector values, but the UI represents all of them as one
         * synthetic Sector option:
         *
         *     Miscellaneous
         *
         * Therefore, when calculating Sector options, collapse:
         *
         *     Russia
         *     CTO
         *     DPS
         *     ...
         *
         * into:
         *
         *     Miscellaneous
         */
        if (
          currentFilterKey === 'sector' &&
          miscellaneousSectors.includes(stringValue)
        ) {
          values.add('Miscellaneous');
        } else {
          values.add(stringValue);
        }
      });
    });

    result[currentFilterKey] =
      Array.from(values).sort();
  });

  return result;
};


export const reconcileCascadingFilters = (
  currentFilters: FilterStateOps,
  cascadingOptions: Record<string, string[]>
): FilterStateOps => {
  let hasChanges = false;

  const nextFilters = { ...currentFilters };

  (
    Object.keys(FILTER_RELATIONSHIP_FIELD_MAP) as Array<
      keyof typeof FILTER_RELATIONSHIP_FIELD_MAP
    >
  )
  .filter(key => key !== 'sector')
  .forEach(key => {
    const allowedValues = cascadingOptions[key];

    // No cascading restriction for this field.
    if (allowedValues === undefined) {
      return;
    }

    const selectedOptions =
      currentFilters[key] as GenericOption[] | undefined;

    if (!selectedOptions?.length) {
      return;
    }

    const allowedSet = new Set(
      allowedValues.map(String)
    );

    const validSelections = selectedOptions.filter(option => {
      const value = String(option.value);

      /*
       * SPECIAL CASE:
       *
       * "Miscellaneous" is a synthetic Sector option.
       *
       * It does not exist as a real Sector value in the
       * relationship records. The real values are stored in:
       *
       * filterOptions.sectorDropdownValues.Miscellaneous
       *
       * Therefore, never remove "Miscellaneous" simply because
       * the cascading relationship engine cannot find the
       * literal value "Miscellaneous".
       */
      // if (
      //   key === 'sector' &&
      //   value === 'Miscellaneous'
      // ) {
      //   return true;
      // }

      return allowedSet.has(value);
    });

    if (validSelections.length !== selectedOptions.length) {
      hasChanges = true;

      nextFilters[key] = validSelections as never;
    }
  });

  return hasChanges ? nextFilters : currentFilters;
};

export const getDerivedAssignmentGroups = (
  currentFilters: FilterStateOps,
  records: any[],
): string[] => {
  const sourceKeys: Array<
    'vendor' | 'team' | 'assignmentGroupListName' | 'subCapability' | 'technology'
  > = [
    'vendor',
    'team',
    'subCapability',
    'technology',
    'assignmentGroupListName'
  ];

  const activeSourceKeys = sourceKeys.filter(key => {
    const selectedValues = getSelectedFilterValues(
      currentFilters[key] as GenericOption[] | undefined
    );

    return selectedValues.length > 0;
  });

  // Nothing is driving Assignment Group.
  if (!activeSourceKeys.length) {
    return [];
  }

  /*
   * For each source field:
   *
   * Vendor:
   *   V1 OR V2
   *
   * Team:
   *   T1 OR T2
   *
   * Assignment Group List:
   *   L1 OR L2
   *
   * Across fields:
   *
   * Vendor AND Team AND List
   */

  const assignmentGroupSets = activeSourceKeys.map(sourceKey => {
    const selectedValues = getSelectedFilterValues(
      currentFilters[sourceKey] as GenericOption[]
    );

    const relationshipField =
      FILTER_RELATIONSHIP_FIELD_MAP[sourceKey];

    const matchingGroups = new Set<string>();

    records.forEach(record => {
      const recordValue = record?.[relationshipField];

      if (
        recordValue === undefined ||
        recordValue === null ||
        recordValue === ''
      ) {
        return;
      }

      const recordValues = Array.isArray(recordValue)
        ? recordValue.map(String)
        : [String(recordValue)];

      const matches = selectedValues.some(selectedValue =>
        recordValues.includes(String(selectedValue))
      );

      if (!matches) {
        return;
      }

      const assignmentGroup = record?.['Assignment Group'];

      if (
        assignmentGroup === undefined ||
        assignmentGroup === null ||
        assignmentGroup === ''
      ) {
        return;
      }

      matchingGroups.add(String(assignmentGroup));
    });

    return matchingGroups;
  });

  /*
   * AND across Vendor / Team / Assignment Group List.
   */
  if (!assignmentGroupSets.length) {
    return [];
  }

  const [firstSet, ...remainingSets] = assignmentGroupSets;

  return Array.from(firstSet).filter(group =>
    remainingSets.every(set => set.has(group))
  );
};

const expandSectorFilter = (params: Record<string, any>, filterOptions: any) => {
  const sectorMap = filterOptions?.sectorDropdownValues || {};
  const { miscellaneousSector, ...restFilters } = params.filter || {};
  const selectedSector = params?.filter?.sector;

  if (!selectedSector) {
    return params;
  }

  const sectorList = Array.isArray(selectedSector)
    ? selectedSector
    : String(selectedSector)
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

  const selectedMiscellaneous = Array.isArray(miscellaneousSector)
    ? miscellaneousSector
    : [];

  const expandedList = sectorList.reduce((acc: any[], sector: string) => {
    if (sector === 'Miscellaneous') {
      return acc.concat(selectedMiscellaneous);
    }

    const directMapped = sectorMap[sector];

    if (Array.isArray(directMapped)) {
      return acc.concat(directMapped);
    }

    const reverseMapped = Object.entries(sectorMap).find(
      ([_, mappedValues]: any) =>
        Array.isArray(mappedValues) &&
        mappedValues.includes(sector)
    );

    if (reverseMapped) {
      return acc.concat(reverseMapped[1] as string[]);
    }

    return acc.concat(sector);
  }, []);

  const uniqueList = [...new Set(expandedList)];

  return {
    ...params,
    filter: {
      ...restFilters,
      sector: uniqueList,
    },
  };
};

export const expandMappedValues = (input: DomainInput, map: Record<string, string[]>): string[] => {
  if (!input) return [];

  const values = Array.isArray(input) ? input : [input];

  const result: string[] = [];

  values.forEach((value) => {
    let matched = false;

    for (const [groupKey, mappedValues] of Object.entries(map)) {
      if (groupKey === value || mappedValues.includes(value)) {
        result.push(groupKey, ...mappedValues);
        matched = true;
        break;
      }
    }

    // ✅ IMPORTANT: if no match found
    if (!matched) {
      result.push(value);
    }
  });

  return Array.from(new Set(result));
};

export const ParentTileMap : Record<string, string> = {
  Incidents: 'incident',
  'Service Requests' : 'service_request',
  'Problem Tickets' : 'problem',
  'Change Requests' : 'change_request'
}

const STATUS_OPTION_KEY_BY_TYPE: Record<string, 'incident_state' | 'problem_state' | 'service_state' | 'change_state'> = {
  incident: 'incident_state',
  problem: 'problem_state',
  service_request: 'service_state',
  change_request: 'change_state',
};

const TICKET_VALUE_KEYS: Record<string, string> = {
  column_contact_type: 'contact_type',
  column_service_offering: 'service_offering',
};

const EMPTY_VALUE_LABELS: Record<string, string> = {
  cmdb_ci: 'CI not available',
  service_offering: 'SO not available',
};

export const getParentTileValue = (
  key: string
): string => {
  return ParentTileMap[key] || 'incident';
};

// The column dropdown API returns { data: { <field>: [values] } }; the table reads [{ id, values }] keyed by column key
const toColumnFilterOptions = (response: any): any[] | null => {
  if (Array.isArray(response)) return response;
  const valuesByField = response?.data ?? response;
  if (!valuesByField || typeof valuesByField !== 'object') return null;

  return Object.entries(valuesByField)
    .filter(([, values]) => Array.isArray(values))
    .map(([id, values]) => ({ id, values: values as any[] }));
};

export const DOMAIN_MAP: Record<string, string[]> = {
  Others: ['Others', 'Other'],
  Global: ['Global', 'Global/Supply Chain', 'Supply Chain'],
  Consumer: ['Consumer', 'Consumer & Suppy Chain', 'Consumer & Supply Chain'],
};

export const PROGRAM_MAP: Record<string, string[]> = {
  Unassigned: ['NA', 'Unassigned', 'FP&A'],
};

const OPS_COLUMN_FILTER_STORAGE_PREFIX = 'unifiedOps_column_filter_state_';

const toEndOfMinute = (iso: string) => {
  const date = new Date(iso);
  date.setSeconds(59, 999);
  return date.toISOString();
};

type SortValues = Array<string | number>;

interface AlertPageRequest {
  searchCursor: SortValues | null;
  direction: 'next' | 'prev';
}

interface AlertPageCursor {
  signature: string;
  page: number;
  firstSortValues: SortValues | null;
  lastSortValues: SortValues | null;
  request: AlertPageRequest;
}

const FIRST_ALERT_PAGE_REQUEST: AlertPageRequest = { searchCursor: null, direction: 'next' };

const getAlertPageRequest = (
  cursor: AlertPageCursor | null,
  signature: string,
  targetPage: number
): AlertPageRequest | null => {
  if (targetPage === 1) return FIRST_ALERT_PAGE_REQUEST;
  if (!cursor || cursor.signature !== signature) return null;
  if (targetPage === cursor.page + 1 && cursor.lastSortValues) {
    return { searchCursor: cursor.lastSortValues, direction: 'next' };
  }
  if (targetPage === cursor.page - 1 && cursor.firstSortValues) {
    return { searchCursor: cursor.firstSortValues, direction: 'prev' };
  }
  if (targetPage === cursor.page) return cursor.request;
  return null;
};

const DEFAULT_ITSM_SORTING = { sortBy: 'sys_updated_on', sortDir: 'desc' };

export const getItsmSortParams = (sortingInfo: { sortBy: string; sortDir: string }) => {
  if (!sortingInfo.sortBy || !sortingInfo.sortDir) {
    return { sortBy: DEFAULT_ITSM_SORTING.sortBy, sortOrder: DEFAULT_ITSM_SORTING.sortDir };
  }
  return {
    sortBy: sortingInfo.sortBy === 'age' ? 'sys_created_on' : sortingInfo.sortBy,
    sortOrder: sortingInfo.sortDir,
  };
};

  const formatFilter = (arr?: { label: string; value: string }[]) => {
    if (!arr || arr.length === 0) return undefined;

    const values = arr.map((x) => x.value);

    return values.length === 1 ? values[0] : values;
  };


  const transformLabels = (arr?: { label: string; value: string }[]) => {
    if (!arr || arr.length === 0) return undefined;
    const transformed = arr.map((item) => {
      switch (item.label) {
        case 'RCA':
          return { ...item, value: 'Root Cause Analysis' };
        case 'P3':
          return { ...item, value: '3 - Moderate' };
        case 'P4':
          return { ...item, value: '4 - Standard' };
        case 'P':
          return { ...item, value: '5 – Regular' };
        default:
          return item;
      }
    });
    return formatFilter(transformed); // returns value(s) only
  };

  // 3️⃣ Boolean filter → single boolean or array of booleans
  const formatBooleanFilterValues = (arr?: { label: string; value: string }[]) => {
    if (!arr || arr.length === 0) return undefined;
    const values = arr.map((item) => String(item.value).toLowerCase() === 'true');
    return values.length === 1 ? values[0] : values;
  };

  // 4️⃣ Lowercase filter → single lowercase string or array of lowercase strings
  const formatLowercaseFilter = (arr?: { label: string; value: string }[]) => {
    if (!arr || arr.length === 0) return undefined;
    const values = arr.map((item) => String(item.value).toLowerCase());
    return values.length === 1 ? values[0] : values;
  };

  const transformAdditionalFilters = (
    filters: Array<{
      field: string;
      value: string;
    }>
  ) => {
    return filters.reduce((acc, filter) => {
      if (filter.field && filter.value) {
        acc[filter.field] = filter.value;
      }

      return acc;
    }, {} as Record<string, string>);
  };

  const FILTER_TRANSFORMERS: Record<string, (val: any) => any> = {
    priority: transformLabels,
    made_sla: formatBooleanFilterValues,
    isDandAIAG: (arr) => {
      if (!arr || arr.length === 0) return undefined;
      const first = arr[0]?.value ?? arr[0];
      if (String(first).toLowerCase() === 'yes' || String(first).toLowerCase() === 'true') return true;
      if (String(first).toLowerCase() === 'no' || String(first).toLowerCase() === 'false') return false;
      return undefined;
    },
    problem_state: transformLabels,
    service_state: transformLabels,
    change_state: transformLabels,
    state: transformLabels,
    known_error: formatLowercaseFilter,
    close_code: (options) => {
      return options.map((opt: any) => {
        if (typeof opt.value === 'string') {
          return {
            value: opt.value,
            operator: 'contains',
          };
        }

        // already structured
        return opt.value;
      });
    },
    additionalFilters: transformAdditionalFilters,
  };

  export const buildFilters = (appliedFilters: any) => {
    return Object.entries(appliedFilters).reduce((acc, [key, value]) => {
      if (!Array.isArray(value) || value.length === 0) return acc;

      const transformer = FILTER_TRANSFORMERS[key] || formatFilter;
      const transformedVal = transformer(value);

      if (transformedVal !== undefined) {
        acc[key] = transformedVal;
      }

      return acc;
    }, {} as Record<string, any>);
  };

  export const getFiltersForTile = (tile: string, appliedFilters: FilterStateOps, activeParentTile: string): FilterStateOps => {
    switch (tile) {

      case 'Change Requests':{
        const { timeRange, change_state } = appliedFilters;
        return {
          timeRange,
          state: change_state
        };
      }
      case 'Service Requests':{
        const { timeRange, service_state } = appliedFilters;
        return {
          timeRange,
          state: service_state
        };
      }
      case 'Problem Tickets':{
        const { timeRange, problem_state } = appliedFilters;
        return {
          timeRange,
          state: problem_state
        };
      }
      case 'Incidents': {
        const { timeRange, incident_state } = appliedFilters;
        return {
          timeRange,
          incident_state
        };
      }

      case 'P3': {
        const { timeRange, incident_state } = appliedFilters;
        return {
          timeRange,
          priority: [
            { label: 'P3', value: '3 - Moderate' }
          ],
          incident_state
        };
      }

      case 'P4': {
        const { timeRange, incident_state } = appliedFilters;
        return {
          timeRange,
          priority: [
            { label: 'P4', value: '4 - Standard' }
          ],
          incident_state
        };
      }

      case 'P5': {
        const { timeRange, incident_state } = appliedFilters;
        return {
          timeRange,
          priority: [{ label: 'P5', value: '5 – Regular' }],
          incident_state
        };
      }

      case 'Closed Incidents': {
        const { timeRange } = appliedFilters;
        return {
          timeRange,
          incident_state: [{ label: 'Closed', value: 'Closed' }]
        };
      }

      case 'SLA Resolution Miss': {
        const { timeRange } = appliedFilters;
        return {
          timeRange,
          resolution_slaHasBreached: [{ label: 'True', value: 'true' }]
        };
      }

      case 'SLA Response Miss': {
        const { timeRange } = appliedFilters;
        return {
          timeRange,
          response_slaHasBreached: [{ label: 'True', value: 'true' }]
        };
      }


      case 'SLA Missed':{
        const { timeRange, change_state } = appliedFilters;
        return {
          timeRange,
          made_sla: [{ label: "False", value: false }],
          state: change_state
        };
      }
      case 'SLA Miss': {
        const { timeRange, service_state } = appliedFilters;
        return {
          timeRange,
          made_sla: [{ label: "False", value: false }],
          state: service_state
        };
      }

      case 'Open': {
        const { timeRange } = appliedFilters;
        let filter = {
          timeRange,
          state: [] as GenericOption[]
        }
        if(activeParentTile === 'Service Requests') {
          filter.state = [
            { label: 'Open', value: 'Open' },
          ];
        } else if(activeParentTile === 'Problem Tickets') {
          filter.state = [
            { label: 'RCA', value: 'Root Cause Analysis' },
            { label: 'Assess', value: 'Assess' },
            { label: 'Fix in Progress', value: 'Fix in Progress' },
            { label: 'New', value: 'New' }
          ];
        }
        return filter
      }

      case 'Known Errors': {
        const { timeRange, problem_state } = appliedFilters;
        return {
          timeRange,
          known_error: [
            { label: 'True', value: 'true' }
          ],
          state: problem_state
        };
      }

      case 'Reactive': {
        const { timeRange, problem_state } = appliedFilters;
        return {
          timeRange,
          u_source: [
            { label: 'Reactive', value: 'Reactive' },
          ],
          state: problem_state
        };
      }


      case 'SLA Met':{
        const { timeRange } = appliedFilters;
        return {
          timeRange,
          made_sla: [{ label: "True", value: true }]
        };
      }

      case 'Made SLA': {
        const { timeRange, problem_state } = appliedFilters;
        return {
          timeRange,
          made_sla: [{ label: "True", value: true }],
          state: problem_state
        };
      }

      case 'RCA': {
        const { timeRange } = appliedFilters;
        return {
          timeRange,
          state: [
            { label: 'RCA', value: 'Root Cause Analysis' },
          ]
        };
      }

      case 'In Progress': {
        const { timeRange } = appliedFilters;
        const filter = {
          timeRange,
          state: [] as GenericOption[]
        }
        if(activeParentTile === 'Change Requests') {
          filter.state = [
            { label: 'Assess', value: 'Assess' },
            { label: 'Authorize', value: 'Authorize' },
            { label: 'Scheduled', value: 'Scheduled' },
            { label: 'Implement', value: 'Implement' },
            { label: 'Review', value: 'Review' }
          ]
        } else if(activeParentTile === 'Service Requests') {
          filter.state = [
            { label: 'Work in Progress', value: 'Work in Progress' },
          ]
        }
        return filter;
      }

      case 'Pending': {
        const { timeRange } = appliedFilters;
        return {
          timeRange,
          state: [
            { label: 'Pending Approval', value: 'Pending Approval' },
          ]
        };
      }

      case 'Closed Complete': {
        const { timeRange } = appliedFilters;
        return {
          timeRange,
          state: [
            { label: 'Closed Complete', value: 'Closed Complete' },
          ]
        };
      }

      case 'Closed Incomplete': {
        const { timeRange } = appliedFilters;
        return {
          timeRange,
          state: [
            { label: 'Closed Incomplete', value: 'Closed Incomplete' },
          ]
        };
      }

      case 'Closed': {
        const { timeRange } = appliedFilters;
        return {
          timeRange,
          state: [
            { label: 'Closed', value: 'Closed' },
          ]
        };
      }

      case 'Not Closed': {
        const { timeRange } = appliedFilters;
        return {
          timeRange,
          state: [
            { label: 'New', value: 'New' },
            { label: 'Assess', value: 'Assess' },
            { label: 'Authorize', value: 'Authorize' },
            { label: 'Scheduled', value: 'Scheduled' },
            { label: 'Implement', value: 'Implement' },
            { label: 'Review', value: 'Review' }
          ]
        };
      }

      case 'Emergency': {
        const { timeRange } = appliedFilters;
        return {
          timeRange,
          type: [
            { label: 'Emergency', value: 'Emergency' },
          ]
        };
      }

      case 'Successful': {
        const { timeRange } = appliedFilters;
        return {
          timeRange,
          close_code: [
            { label: 'Successful', value: 'Successful' },
          ]
        };
      }

      case 'Unsuccessful': {
        const { timeRange } = appliedFilters;
        return {
          timeRange,
          close_code: [
            { label: 'Unsuccessful', value: 'Unsuccessful' },
          ]
        };
      }

      default:
        return DEFAULT_FILTERS;
    }
  };

export const useUnifiedIncidents = () => {
  //Ultimate Truth
  const [liveTile, setLiveTile] = useState('Incidents');

  const [activeParentTile, setActiveParentTile] = useState(ITSM_TILES_CONFIG[0].key);
  const [activeSubTile, setActiveSubTile] = useState<string>('Incidents');

  const [activeTile, setActiveTile] = useState('P3');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchText, setSearchText] = useState('');
  const [simpleSearchText, setSimpleSearchText] = useState('');
  const [sortingInfo, setSortingInfoState] = useState(DEFAULT_ITSM_SORTING);
  
  const setSortingInfo = (value: { sortBy: string; sortDir: string }) => {
    setSortingInfoState(value?.sortBy && value?.sortDir ? value : DEFAULT_ITSM_SORTING);
  };
const [appliedFilters, setAppliedFilters] = useState<FilterStateOps>(DEFAULT_FILTERS);
  const [filterOptions, setFilterOptions] = useState<any>(DEFAULT_FILTER_OPTIONS);
   const [filterRelationshipRecords, setFilterRelationshipRecords] =
  useState<any[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [incidentSummary, setIncidentSummary] = useState<any>(null);
  const [problemSummary, setProblemSummary] = useState<any>(null);
  const [serviceSummary, setServiceSummary] = useState<any>(null);
  const [changeSummary, setChangeSummary] = useState<any>(null);
  const [data, setData] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [isFiltersHydrated, setIsFiltersHydrated] = useState(false);
  const [autoRefreshInterval, setAutoRefreshInterval] = useState<number | null>(() => {
    const saved = sessionStorage.getItem('unifiedAlertsAutoRefresh');
    return saved ? Number(saved) : 5;
  });
  const [fixedStartDate, setFixedStartDate] = useState<string | undefined>(undefined);
  const [fixedEndDate, setFixedEndDate] = useState<string | undefined>(undefined);

  const [trendSector, setTrendSector] = useState<any>(null);
  const [trendProgram, setTrendProgram] = useState<any>(null);
  const [mostImpactedApps, setMostImpactedApps] = useState<any[]>([]);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const alertPageCursorRef = useRef<AlertPageCursor | null>(null);
  const loadAlertsRef = useRef<((dateParams?: DateParams) => Promise<void>) | null>(null);
  const loadAllRef = useRef<((dateParams?: DateParams) => void) | null>(null);
  const lastLoadedTileRef = useRef(`${liveTile}|${activeTile}`);
  const isPageNavigationRef = useRef(false);
  const [isChartsLoading, setIsChartsLoading] = useState(false);
  const latestStateRef = useRef<any>({});
  const alertsListRequestIdRef = useRef(0);
  const [groupBy, setGroupBy] = useState<string>('none');
  const [isGrouped, setIsGrouped] = useState(false);
  const [searchAfter, setSearchAfter] = useState<any>(null);
  const [isLoadingMoreChildren, setIsLoadingMoreChildren] = useState(false);
  const [loadingGroupId, setLoadingGroupId] = useState<string | null>(null);
  const [isDataOpsAdmin, setIsDataOpsAdmin] = useState<boolean>(false);
  const [columnFilterVersion, setColumnFilterVersion] = useState(0);
  const columnFilterStorageKey = `${OPS_COLUMN_FILTER_STORAGE_PREFIX}${activeParentTile}`;
  const columnFilters = useMemo(
    () => loadColumnFilterPayload(columnFilterStorageKey),
    [columnFilterStorageKey, columnFilterVersion]
  );
  const columnFiltersRef = useRef<ColumnFilterPayloadItem[]>(columnFilters);
  columnFiltersRef.current = columnFilters;

  const columnFilterOptionsRef = useRef<Promise<any[] | null> | null>(null);
  const [originalfilterOptions, setOriginalFilterOptions] = useState<any>(DEFAULT_FILTER_OPTIONS); 

  const persistDateRangeToSession = (filters: FilterStateOps) => {
    if (filters.timeRange) {
      sessionStorage.setItem('unifiedAlertsDateRangeType', filters.timeRange);
    }

    if (filters.timeRange === 'custom' && filters.startDate && filters.endDate) {
      sessionStorage.setItem('unifiedAlertsStartDate', filters.startDate);
      sessionStorage.setItem('unifiedAlertsEndDate', filters.endDate);
    } else {
      sessionStorage.removeItem('unifiedAlertsStartDate');
      sessionStorage.removeItem('unifiedAlertsEndDate');
    }
    window.dispatchEvent(new Event('unifiedAlertsDateRangeHydrated'));
  };

  const withCustomDates = (filters: FilterStateOps): FilterStateOps => {
    if (filters.timeRange !== 'custom') return filters;

    return {
      ...filters,
      startDate: filters.startDate ?? sessionStorage.getItem('unifiedAlertsStartDate') ?? undefined,
      endDate: filters.endDate ?? sessionStorage.getItem('unifiedAlertsEndDate') ?? undefined,
    };
  };

  const getDateParams = (): DateParams => {
    const pewsType = sessionStorage.getItem('unifiedAlertsDateRangeType');
    if (pewsType) {
      if (pewsType === 'more_than_90_days') {
        const olderThan90End = new Date();
        olderThan90End.setDate(olderThan90End.getDate() - 90);

        return {
          endDate: olderThan90End.toISOString(),
        };
      }

      if (pewsType === 'custom') {
        const start = sessionStorage.getItem('unifiedAlertsStartDate');
        const end = sessionStorage.getItem('unifiedAlertsEndDate');
        if (start && end) return { startDate: start, endDate: toEndOfMinute(end) };
      } else {
        const now = new Date();
        const endDate = now.toISOString();
        const parseValue = (v: string) => {
          const match = v.match(/^(\d+)\s*(m|min|minutes|h|hr|hour|d|days|day|w|weeks|week)$/i);
          if (!match) return null;
          const num = parseInt(match[1]);
          const unit = match[2].toLowerCase();

          let multiplier = 1;
          if (unit.startsWith('m') && !unit.startsWith('mi')) multiplier = 60 * 1000;
          else if (unit.startsWith('mi')) multiplier = 60 * 1000;
          else if (unit.startsWith('h')) multiplier = 60 * 60 * 1000;
          else if (unit.startsWith('d')) multiplier = 24 * 60 * 60 * 1000;
          else if (unit.startsWith('w')) multiplier = 7 * 24 * 60 * 60 * 1000;

          return num * multiplier;
        };
        const rangeMap: Record<string, number> = {
          '5m': 5 * 60 * 1000,
          '10m': 10 * 60 * 1000,
          '15m': 15 * 60 * 1000,
          '30m': 30 * 60 * 1000,
          '1h': 60 * 60 * 1000,
          '24h': 24 * 60 * 60 * 1000,
          '7d': 7 * 24 * 60 * 60 * 1000,
          '30d': 30 * 24 * 60 * 60 * 1000,
          '90d': 90 * 24 * 60 * 60 * 1000,
          today:
            now.getTime() - new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime(),
        };
        const offset = rangeMap[pewsType] ?? parseValue(pewsType);
        if (offset !== undefined && offset !== null) {
          return {
            startDate: new Date(now.getTime() - offset).toISOString(),
            endDate,
          };
        }
      }
    }
    if (appliedFilters.timeRange === 'more_than_90_days') {
      const olderThan90End = new Date();
      olderThan90End.setDate(olderThan90End.getDate() - 90);

      return {
        endDate: olderThan90End.toISOString(),
      };
    }

    if (
      appliedFilters.timeRange === 'custom' &&
      appliedFilters.startDate &&
      appliedFilters.endDate
    ) {
      return {
        startDate: appliedFilters.startDate,
        endDate: toEndOfMinute(appliedFilters.endDate),
      };
    }
    return timeRangeToDates(appliedFilters.timeRange);
  };

  const isOlderThan90DaysSelected = () => {
    const sessionRange = sessionStorage.getItem('unifiedAlertsDateRangeType');

    return (
      sessionRange === 'more_than_90_days' ||
      appliedFilters.timeRange === 'more_than_90_days'
    );
  };

  const getDatePayloadParams = (dateParams?: DateParams) => {
    const resolvedDateParams = dateParams ?? getDateParams();

    if (isOlderThan90DaysSelected()) {
      return {
        endDate: resolvedDateParams.endDate || getDateParams().endDate,
      };
    }

    return {
      startDate: dateParams?.startDate || fixedStartDate || getDateParams().startDate,
      endDate: dateParams?.endDate || fixedEndDate || getDateParams().endDate,
    };
  };

  useEffect(() => {
    const handleDateRangeChanged = () => {
      if (alertPageCursorRef) {
        alertPageCursorRef.current = null;
      }
      loadAllRef.current?.(getDateParams());
      loadAlertsRef.current?.(getDateParams());
    };

    window.addEventListener('unifiedAlertsDateRangeChanged', handleDateRangeChanged);
    return () =>
      window.removeEventListener('unifiedAlertsDateRangeChanged', handleDateRangeChanged);
  }, []);

  const loadTrendData = async (dateParams?: DateParams) => {
    try {
      setIsChartsLoading(true);

      const params: any = dateParams ?? getDateParams();
      params.search = searchText || undefined;
      params.type = ITSMTypes[activeParentTile as keyof typeof ITSMTypes] || 'incident';
      // applyAdvanceFilters(params)
      const dynamicFilters = buildFilters(appliedFilters);
      let finalFilters = dynamicFilters;
      // 🚨 PRE-FLIGHT CHECK (BEFORE API CALL)
      // This is required because currently we are fetching details based on parent-child logic
      // so what is happening was if i click on some child, parent also changes (which is correct)
      /// Parent should also chnage based on filter conditions but what if we select on predefined child
      ///  conditions, and for those filters also parent changes ? That shouldnt happen
      /// Hence im adding condition such that only in case of predefined child filter conditions
      /// I am setting empty filters. (Because if triggered value of child and parent will become same)
      /// Becasuse filter conditions are also same
      /// Hence this I am setting empty filters.
      // if (shouldBlockApiCall(dynamicFilters, liveTile)) {
      //   console.warn("Filters removed due to rule match", dynamicFilters, liveTile);
      //   finalFilters = {};
      // }
      if (Object.keys(dynamicFilters).length) {
        params.filter = {
          ...(params.filter || {}),
          ...finalFilters,
        };
      }

      const updatedParams = expandSectorFilter(params, filterOptions);

      const domainInput = updatedParams?.filter?.domain;
      const programInput = updatedParams?.filter?.program;

      const transformedParams = {
        ...updatedParams,
        parentTile: getParentTileValue(activeParentTile),
        filter: {
          ...updatedParams.filter,

          ...(domainInput
            ? {
              domain: expandMappedValues(domainInput, DOMAIN_MAP),
            }
            : {}),

          ...(programInput
            ? {
              program: expandMappedValues(programInput, PROGRAM_MAP),
            }
            : {}),
        },
      };

      const { assignmentGroupListName, ...cleanFilter } = transformedParams.filter;

      const cleanedTransformedParams = {
        ...transformedParams,
        filter: cleanFilter
      };

      const [sectorData, programData] = await Promise.all([
        unifiedAlertsService.getOpsTrendBySector(cleanedTransformedParams),
        unifiedAlertsService.getOpsTrendByProgram(cleanedTransformedParams),
      ]);
      const trendData = transformTrendData(sectorData?.data as any[], 'sectors', liveTile);
      if (trendData?.stats) {
        trendData.stats.totalAlerts = sectorData?.totalUnique || 0;
        trendData.stats.topSector = sectorData?.peakSector;
        trendData.stats.peakDaySubtitle = `${sectorData?.peakDayUnique} Records`;
        trendData.stats.peakDay = sectorData?.peakDay;

        if (sectorData?.trendPercent !== null && sectorData?.trendPercent !== undefined) {
          trendData.stats.avgSubtitle = `${sectorData.trendPercent}% ${sectorData.trend}`;
        } else {
          trendData.stats.avgSubtitle = `${sectorData?.trend}`;
        }
        trendData.stats.avgTrend = sectorData?.trend;
      }

      setTrendSector(trendData);

      const proData = transformTrendData(programData?.data as any[], 'program', liveTile);
      if (proData?.stats) {
        proData.stats.totalAlerts = programData?.totalUnique || 0;
        proData.stats.topProgram = programData?.peakSector;
        proData.stats.peakDaySubtitle = `${programData?.peakDayUnique} Records`;
        proData.stats.peakDay = programData?.peakDay;
        proData.stats.avgPerDay = programData?.avgUniquePerDay;

        if (programData?.trendPercent !== null && programData?.trendPercent !== undefined) {
          proData.stats.avgSubtitle = `${programData.trendPercent}% ${programData.trend}`;
        } else {
          proData.stats.avgSubtitle = `${programData?.trend}`;
        }
        proData.stats.avgTrend = programData?.trend;
      }

      setTrendProgram(proData);
    } catch (error) {
      console.error('Error loading trend data:', error);
    } finally {
      setIsChartsLoading(false);
    }
  };

  const loadMostImpactedApps = async (dateParams?: DateParams) => {
    try {
      const params: any = dateParams ?? getDateParams();
      params.search = searchText || undefined;
      params.type = ITSMTypes[activeParentTile as keyof typeof ITSMTypes] || 'incident';
      // applyAdvanceFilters(params)
      const dynamicFilters = buildFilters(appliedFilters);
      let finalFilters = dynamicFilters;
      // 🚨 PRE-FLIGHT CHECK (BEFORE API CALL)
      // This is required because currently we are fetching details based on parent-child logic
      // so what is happening was if i click on some child, parent also changes (which is correct)
      /// Parent should also chnage based on filter conditions but what if we select on predefined child
      ///  conditions, and for those filters also parent changes ? That shouldnt happen
      /// Hence im adding condition such that only in case of predefined child filter conditions
      /// I am setting empty filters. (Because if triggered value of child and parent will become same)
      /// Becasuse filter conditions are also same
      /// Hence this I am setting empty filters.
      // if (shouldBlockApiCall(dynamicFilters, liveTile)) {
      //   console.warn("Filters removed due to rule match", dynamicFilters, liveTile);
      //   finalFilters = {};
      // }
      if (Object.keys(dynamicFilters).length) {
        params.filter = {
          ...(params.filter || {}),
          ...finalFilters,
        };
      }

      const updatedParams = expandSectorFilter(params, filterOptions);

      const domainInput = updatedParams?.filter?.domain;
      const programInput = updatedParams?.filter?.program;

      const transformedParams = {
        ...updatedParams,
        parentTile: getParentTileValue(activeParentTile),
        filter: {
          ...updatedParams.filter,

          ...(domainInput
            ? {
              domain: expandMappedValues(domainInput, DOMAIN_MAP),
            }
            : {}),

          ...(programInput
            ? {
              program: expandMappedValues(programInput, PROGRAM_MAP),
            }
            : {}),
        },
      };

      const { assignmentGroupListName, ...cleanFilter } = transformedParams.filter;

      const cleanedTransformedParams = {
        ...transformedParams,
        filter: cleanFilter
      };


      const data = await unifiedAlertsService.getOpsMostImpactedApps(cleanedTransformedParams);
      setMostImpactedApps(data);
    } catch (error) {
      console.error('Error loading most impacted apps:', error);
    }
  };

  useEffect(() => {
    latestStateRef.current = {
      page,
      pageSize,
      searchText,
      activeTile,
      sortingInfo,
      appliedFilters,
      liveTile,
      activeParentTile,
      fixedStartDate,
      fixedEndDate,
      groupBy,
      searchAfter,
      filterOptions,
    };
  }, [
    page,
    pageSize,
    searchText,
    activeTile,
    sortingInfo,
    appliedFilters,
    liveTile,
    activeParentTile,
    fixedStartDate,
    fixedEndDate,
    groupBy,
    searchAfter,
    filterOptions,
  ]);

  const getGroupedValue = (group: any, groupByValue: string) => {
    return (
      group?.[groupByValue] ??
      group?.groupValue ??
      group?.key ??
      group?.value ??
      group?.name ??
      'Unassigned'
    );
  };

  const getGroupedCount = (group: any, groupByValue: string) => {
    const countField = `${groupByValue}Count`;
    return (
      group?.[countField] ??
      group?.totalChildrenCount ??
      group?.totalCount ??
      group?.count ??
      group?.children?.length ??
      0
    );
  };

  const normalizeGroupedRows = (groups: any[], groupByValue: string) => {
    return (groups || []).map((group: any) => {
      const groupValue = getGroupedValue(group, groupByValue);
      const totalCount = getGroupedCount(group, groupByValue);
      const countField = `${groupByValue}Count`;

      return {
        ...group,
        [groupByValue]: groupValue,
        [countField]: group?.[countField] ?? totalCount,
        children: Array.isArray(group?.children) ? group.children : [],
        totalCount,
        totalChildrenCount: group?.totalChildrenCount ?? totalCount,
        isParent: true,
      };
    });
  };

  const loadMoreChildren = useCallback(
    async (groupByValue: string, groupValue: string, currentCount: number, parentRow: any) => {
      setIsLoadingMoreChildren(true);
      setLoadingGroupId(`${groupByValue}-${groupValue}`);

      try {
        const state = latestStateRef.current;
        const tileFilters = getFiltersForTile(state.liveTile, appliedFilters, activeParentTile);
        const dynamicFilters = buildFilters({ ...(state.appliedFilters || appliedFilters), ...tileFilters });
        const hasFilters = (obj: object) => Object.keys(obj).length > 0;
        const params: any = {
          pageSize: 50,
          groupBy: groupByValue,
          groupValue,
          afterCount: currentCount,
          from: currentCount,
          search: state.searchText || undefined,
          severity: state.liveTile,
          ...getDatePayloadParams(),
          ...(hasFilters(dynamicFilters) && {
            filter: dynamicFilters,
          }),
          ...(state.sortingInfo?.sortBy &&
            state.sortingInfo?.sortDir && {
            sortBy: state.sortingInfo.sortBy,
            sortOrder: state.sortingInfo.sortDir,
          }),
          ...(columnFiltersRef.current.length > 0 && { columnFilters: columnFiltersRef.current }),
        };

        const updatedParams = expandSectorFilter(params, state.filterOptions || filterOptions);

        const domainInput = updatedParams?.filter?.domain;
        const programInput = updatedParams?.filter?.program;

        const transformedParams = {
          ...updatedParams,
          parentTile: getParentTileValue(activeParentTile),
          filter: {
            ...updatedParams.filter,

            ...(domainInput
              ? {
                domain: expandMappedValues(domainInput, DOMAIN_MAP),
              }
              : {}),

            ...(programInput
              ? {
                program: expandMappedValues(programInput, PROGRAM_MAP),
              }
              : {}),
          },
        };

      const { assignmentGroupListName, ...cleanFilter } = transformedParams.filter;

      const cleanedTransformedParams = {
        ...transformedParams,
        filter: cleanFilter
      };


        const response = await unifiedAlertsService.getGroupedChildren(cleanedTransformedParams);

        const responseData = response?.data;
        const groupedChildren =
          responseData?.data?.groups?.[0]?.children ||
          responseData?.groups?.[0]?.children ||
          response?.groups?.[0]?.children;
        const directChildren =
          (Array.isArray(responseData) ? responseData : undefined) ||
          responseData?.children ||
          responseData?.data?.children ||
          responseData?.data?.data ||
          (Array.isArray(responseData?.data) ? responseData.data : undefined) ||
          response?.children;
        const children = groupedChildren || directChildren || [];
        const normalizedChildren = Array.isArray(children) ? children : [];
        const groupMeta =
          responseData?.data?.groups?.[0] ||
          responseData?.groups?.[0] ||
          response?.groups?.[0] ||
          {};
        const totalChildren =
          responseData?.totalChildrenCount ||
          responseData?.data?.totalChildrenCount ||
          groupMeta?.totalChildrenCount ||
          groupMeta?.[`${groupByValue}Count`] ||
          response?.totalChildrenCount ||
          response?.pagination?.total ||
          parentRow?.totalChildrenCount ||
          parentRow?.totalCount;
        const nextOffset =
          responseData?.nextOffset ??
          responseData?.data?.nextOffset ??
          groupMeta?.nextOffset ??
          response?.nextOffset ??
          currentCount + normalizedChildren.length;
        const hasMore =
          responseData?.hasMore ??
          responseData?.data?.hasMore ??
          groupMeta?.hasMore ??
          response?.hasMore ??
          (typeof totalChildren === 'number' ? nextOffset < totalChildren : false);

        return {
          children: normalizedChildren,
          hasMore,
          nextOffset,
        };
      } catch (error) {
        console.error('Error loading more children:', error);
        return null;
      } finally {
        setIsLoadingMoreChildren(false);
        setLoadingGroupId(null);
      }
    },
    [
      appliedFilters,
      filterOptions,
      searchText,
      sortingInfo,
      liveTile,
      activeParentTile,
      fixedStartDate,
      fixedEndDate,
    ],
  );

  const stripRedundantFilters = (filters: Record<string, any>, liveTile: string) => {
    const rules = BLOCK_API_RULES.filter((r) => r.liveTile === liveTile);
    if (!rules.length) return filters;

    const cleaned = { ...filters };

    const normalize = (val: any) => {
      if (val == null) return val;
      if (typeof val === 'string') return val.trim().toLowerCase();
      return val;
    };

    const extractValues = (val: any) => {
      if (val == null) return [];

      if (Array.isArray(val)) {
        return val.map((v) => (typeof v === 'object' ? v.value : v));
      }

      if (typeof val === 'object') {
        return [val.value];
      }

      return [val];
    };

    const isMatch = (actualValue: any, ruleValue: any) => {
      const actualArr = extractValues(actualValue).map(normalize);
      const ruleArr = extractValues(ruleValue).map(normalize);

      if (actualArr.length !== ruleArr.length) return false;

      return actualArr.every((v) => ruleArr.includes(v));
    };

    rules.forEach((rule) => {
      Object.entries(rule.conditions).forEach(([field, ruleValue]) => {
        const actualValue = cleaned[field];

        if (actualValue === undefined) return;

        if (isMatch(actualValue, ruleValue)) {
          if (liveTile === "Open" && field === "problem_state") {
            return;
          } else if (liveTile === "Not Closed" && field === "change_state") {
            return;
          }

          delete cleaned[field];
        }
      });
    });

    return cleaned;
  };

  const loadPriority = async (dateParams?: DateParams) => {
    try {
      const params: any = dateParams ?? getDateParams();
      params.search = searchText || undefined;
      // applyAdvanceFilters(params)

      const dynamicFilters = buildFilters(appliedFilters);

      const finalFilters = stripRedundantFilters(dynamicFilters, liveTile);
      if (Object.keys(finalFilters).length) {
        params.filter = finalFilters;
      } else {
        delete params.filter;
      }
      const updatedParams = expandSectorFilter(params, filterOptions);
      // Use new ops-summary endpoint for ITSM type to get comparison/trend data
      const domainInput = updatedParams?.filter?.domain;
      const programInput = updatedParams?.filter?.program;

      const transformedParams = {
        ...updatedParams,
        parentTile: getParentTileValue(activeParentTile),
        filter: {
          ...updatedParams.filter,

          ...(domainInput
            ? {
              domain: expandMappedValues(domainInput, DOMAIN_MAP),
            }
            : {}),

          ...(programInput
            ? {
              program: expandMappedValues(programInput, PROGRAM_MAP),
            }
            : {}),
        },
      };

      const { assignmentGroupListName, ...cleanFilter } = transformedParams.filter;

      const cleanedTransformedParams = {
        ...transformedParams,
        filter: cleanFilter
      };

      const data = await unifiedAlertsService.getIncidentSummary(cleanedTransformedParams);

      // Transform ops-summary response to match SeverityTiles expectations
      const transformedData = {
        p3: data?.comparison?.priority
          ? {
            current: data.comparison.priority?.['3 - Moderate']?.current ?? 0,
            previous: data.comparison.priority?.['3 - Moderate']?.previous ?? 0,
            difference: data.comparison.priority?.['3 - Moderate']?.difference ?? 0,
            trend: data.comparison.priority?.['3 - Moderate']?.trend ?? 'NO_CHANGE',
          }
          : null,
        p4: data?.comparison?.priority
          ? {
            current: data.comparison.priority?.['4 - Standard']?.current ?? 0,
            previous: data.comparison.priority?.['4 - Standard']?.previous ?? 0,
            difference: data.comparison.priority?.['4 - Standard']?.difference ?? 0,
            trend: data.comparison.priority?.['4 - Standard']?.trend ?? 'NO_CHANGE',
          }
          : null,
        p5: data?.comparison?.priority
          ? {
            current: data.comparison.priority?.['5 – Regular']?.current ?? 0,
            previous: data.comparison.priority?.['5 – Regular']?.previous ?? 0,
            difference: data.comparison.priority?.['5 – Regular']?.difference ?? 0,
            trend: data.comparison.priority?.['5 – Regular']?.trend ?? 'NO_CHANGE',
          }
          : null,
        closed: data?.comparison?.closed
          ? {
            current: data.comparison.closed.current || 0,
            previous: data.comparison.closed.previous || 0,
            difference: data.comparison.closed.difference || 0,
            trend: data.comparison.closed.trend || 'NO_CHANGE',
          }
          : null,
        slaResponseMissed: data?.comparison?.slaResponseMissed
          ? {
            current: data?.comparison.slaResponseMissed.current || 0,
            previous: data?.comparison.slaResponseMissed.previous || 0,
            difference: data?.comparison.slaResponseMissed.difference || 0,
            trend: data?.comparison.slaResponseMissed.trend || 'NO_CHANGE',
          } : null,
        slaResolutionMissed: data?.comparison?.slaResolutionMissed
          ? {
            current: data?.comparison.slaResolutionMissed.current || 0,
            previous: data?.comparison.slaResolutionMissed.previous || 0,
            difference: data?.comparison.slaResolutionMissed.difference || 0,
            trend: data?.comparison.slaResolutionMissed.trend || 'NO_CHANGE',
          }
          : null,
      };

      setIncidentSummary(transformedData);
      const priorityBuckets = data?.comparison?.priority ?? {};
      setSummary((prev: any) => ({
        ...prev,
        incidents: aggregateSummary(priorityBuckets, Object.keys(priorityBuckets)),
      }));
    } catch (error) {
      console.error('Error loading summary:', error);
    }
  };

  const loadProblems = async (dateParams?: DateParams) => {
    try {
      const params: any = dateParams ?? getDateParams();
      params.search = searchText || undefined;
      // applyAdvanceFilters(params)
      const dynamicFilters = buildFilters(appliedFilters);

      const finalFilters = stripRedundantFilters(dynamicFilters, liveTile);
      if (Object.keys(finalFilters).length) {
        params.filter = finalFilters;
      } else {
        delete params.filter;
      }

      const updatedParams = expandSectorFilter(params, filterOptions);
      // Use new ops-summary endpoint for ITSM type to get comparison/trend data
      const domainInput = updatedParams?.filter?.domain;
      const programInput = updatedParams?.filter?.program;

      const transformedParams = {
        ...updatedParams,
        parentTile: getParentTileValue(activeParentTile),
        filter: {
          ...updatedParams.filter,

          ...(domainInput
            ? {
              domain: expandMappedValues(domainInput, DOMAIN_MAP),
            }
            : {}),

          ...(programInput
            ? {
              program: expandMappedValues(programInput, PROGRAM_MAP),
            }
            : {}),
        },
      };

       const { assignmentGroupListName, ...cleanFilter } = transformedParams.filter;

      const cleanedTransformedParams = {
        ...transformedParams,
        filter: cleanFilter
      };

      const data = await unifiedAlertsService.getProblemSummary(cleanedTransformedParams);

      // // Transform ops-summary response to match SeverityTiles expectations
      const transformedData = {
        all: data?.comparison?.all
          ? {
            current: data?.comparison.all.current || 0,
            previous: data?.comparison.all.previous || 0,
            difference: data?.comparison.all.difference || 0,
            trend: data?.comparison.all.trend || 'NO_CHANGE',
          }
          : null,
        open: data?.comparison?.open
          ? {
            current: data?.comparison.open.current || 0,
            previous: data?.comparison.open.previous || 0,
            difference: data?.comparison.open.difference || 0,
            trend: data?.comparison.open.trend || 'NO_CHANGE',
          }
          : null,

        reactive: data?.comparison?.reactive
          ? {
            current: data?.comparison.reactive.current || 0,
            previous: data?.comparison.reactive.previous || 0,
            difference: data?.comparison.reactive.difference || 0,
            trend: data?.comparison.reactive.trend || 'NO_CHANGE',
          }
          : null,

        rca: data?.comparison?.rca
          ? {
            current: data?.comparison.rca.current || 0,
            previous: data?.comparison.rca.previous || 0,
            difference: data?.comparison.rca.difference || 0,
            trend: data?.comparison.rca.trend || 'NO_CHANGE',
          }
          : null,

        madeSla: data?.comparison?.madeSla
          ? {
            current: data?.comparison.madeSla.current || 0,
            previous: data?.comparison.madeSla.previous || 0,
            difference: data?.comparison.madeSla.difference || 0,
            trend: data?.comparison.madeSla.trend || 'NO_CHANGE',
          }
          : null,

        knowErrors: data?.comparison?.knowErrors
          ? {
            current: data?.comparison.knowErrors.current || 0,
            previous: data?.comparison.knowErrors.previous || 0,
            difference: data?.comparison.knowErrors.difference || 0,
            trend: data?.comparison.knowErrors.trend || 'NO_CHANGE',
          }
          : null,
      };

      setProblemSummary(transformedData);
      setSummary((prev: any) => ({
        ...prev,
        problemTickets: aggregateSummary(transformedData, ["all"]),
      }));
    } catch (error) {
      console.error('Error loading summary:', error);
    }
  };

  const loadServiceSummary = async (dateParams?: DateParams) => {
    try {
      const params: any = dateParams ?? getDateParams();
      params.search = searchText || undefined;
      // applyAdvanceFilters(params)
      const dynamicFilters = buildFilters(appliedFilters);

      const finalFilters = stripRedundantFilters(dynamicFilters, liveTile);
      if (Object.keys(finalFilters).length) {
        params.filter = finalFilters;
      } else {
        delete params.filter;
      }

      const updatedParams = expandSectorFilter(params, filterOptions);
      // Use new ops-summary endpoint for ITSM type to get comparison/trend data
      const domainInput = updatedParams?.filter?.domain;
      const programInput = updatedParams?.filter?.program;

      const transformedParams = {
        ...updatedParams,
        parentTile: getParentTileValue(activeParentTile),
        filter: {
          ...updatedParams.filter,

          ...(domainInput
            ? {
              domain: expandMappedValues(domainInput, DOMAIN_MAP),
            }
            : {}),

          ...(programInput
            ? {
              program: expandMappedValues(programInput, PROGRAM_MAP),
            }
            : {}),
        },
      };

      const { assignmentGroupListName, ...cleanFilter } = transformedParams.filter;

      const cleanedTransformedParams = {
        ...transformedParams,
        filter: cleanFilter
      };


      const data = await unifiedAlertsService.getServiceSummary(cleanedTransformedParams);

      const stateMapping: Record<string, string> = {
        'Work in Progress': 'wip',
        'Closed Complete': 'cc',
        Open: 'open',
        'Pending Approval': 'pa',
        'Closed Incomplete': 'ci',
        'Closed Rejected': 'cr',
        Escalated: 'escalated',
      };

      const serviceState = transformedParams?.filter?.service_state;

      const serveArray = (
        Array.isArray(serviceState)
          ? serviceState
          : serviceState
            ? [serviceState]
            : []
      )
        .map((state: string) => stateMapping[state])
        .filter(Boolean);

      // Transform ops-summary response to match SeverityTiles expectations
      const transformedData = {
        wip: data?.comparison?.state
          ? {
            current: data.comparison.state?.['Work in Progress']?.current ?? 0,
            previous: data.comparison.state?.['Work in Progress']?.previous ?? 0,
            difference: data.comparison.state?.['Work in Progress']?.difference ?? 0,
            trend: data.comparison.state?.['Work in Progress']?.trend ?? 'NO_CHANGE',
          }
          : null,
        cc: data?.comparison?.state
          ? {
            current: data.comparison.state?.['Closed Complete']?.current ?? 0,
            previous: data.comparison.state?.['Closed Complete']?.previous ?? 0,
            difference: data.comparison.state?.['Closed Complete']?.difference ?? 0,
            trend: data.comparison.state?.['Closed Complete']?.trend ?? 'NO_CHANGE',
          }
          : null,
        open: data?.comparison?.state
          ? {
            current: data.comparison.state?.['Open']?.current ?? 0,
            previous: data.comparison.state?.['Open']?.previous ?? 0,
            difference: data.comparison.state?.['Open']?.difference ?? 0,
            trend: data.comparison.state?.['Open']?.trend ?? 'NO_CHANGE',
          }
          : null,
        pa: data?.comparison?.state
          ? {
            current: data.comparison.state?.['Pending Approval']?.current ?? 0,
            previous: data.comparison.state?.['Pending Approval']?.previous ?? 0,
            difference: data.comparison.state?.['Pending Approval']?.difference ?? 0,
            trend: data.comparison.state?.['Pending Approval']?.trend ?? 'NO_CHANGE',
          }
          : null,
        ci: data?.comparison?.state
          ? {
            current: data.comparison.state?.['Closed Incomplete']?.current ?? 0,
            previous: data.comparison.state?.['Closed Incomplete']?.previous ?? 0,
            difference: data.comparison.state?.['Closed Incomplete']?.difference ?? 0,
            trend: data.comparison.state?.['Closed Incomplete']?.trend ?? 'NO_CHANGE',
          }
          : null,
        cr: data?.comparison?.state
          ? {
            current: data.comparison.state?.['Closed Rejected']?.current ?? 0,
            previous: data.comparison.state?.['Closed Rejected']?.previous ?? 0,
            difference: data.comparison.state?.['Closed Rejected']?.difference ?? 0,
            trend: data.comparison.state?.['Closed Rejected']?.trend ?? 'NO_CHANGE',
          }
          : null,
        escalated: data?.comparison?.state
          ? {
            current: data.comparison.state?.['Escalated']?.current ?? 0,
            previous: data.comparison.state?.['Escalated']?.previous ?? 0,
            difference: data.comparison.state?.['Escalated']?.difference ?? 0,
            trend: data.comparison.state?.['Escalated']?.trend ?? 'NO_CHANGE',
          }
          : null,
      };

      setServiceSummary(transformedData);

      setSummary((prev: any) => ({
        ...prev,
        service: aggregateSummary(transformedData, serveArray),
      }));
    } catch (error) {
      console.error('Error loading summary:', error);
    }
  };

  const loadChangeSummary = async (dateParams?: DateParams) => {
    try {
      const params: any = dateParams ?? getDateParams();
      params.search = searchText || undefined;
      // applyAdvanceFilters(params)
      const dynamicFilters = buildFilters(appliedFilters);

      const finalFilters = stripRedundantFilters(dynamicFilters, liveTile);
      if (Object.keys(finalFilters).length) {
        params.filter = finalFilters;
      } else {
        delete params.filter;
      }

      const updatedParams = expandSectorFilter(params, filterOptions);
      // Use new ops-summary endpoint for ITSM type to get comparison/trend data
      const domainInput = updatedParams?.filter?.domain;
      const programInput = updatedParams?.filter?.program;

      const transformedParams = {
        ...updatedParams,
        parentTile: getParentTileValue(activeParentTile),
        filter: {
          ...updatedParams.filter,

          ...(domainInput
            ? {
              domain: expandMappedValues(domainInput, DOMAIN_MAP),
            }
            : {}),

          ...(programInput
            ? {
              program: expandMappedValues(programInput, PROGRAM_MAP),
            }
            : {}),
        },
      };

      const { assignmentGroupListName, ...cleanFilter } = transformedParams.filter;

      const cleanedTransformedParams = {
        ...transformedParams,
        filter: cleanFilter
      };

      const data = await unifiedAlertsService.getChangeSummary(cleanedTransformedParams);

      // Transform ops-summary response to match SeverityTiles expectations
      const transformedData = {
        all: data?.comparison?.all
          ? {
            current: data.comparison.all?.current ?? 0,
            previous: data.comparison.all?.previous ?? 0,
            difference: data.comparison.all?.difference ?? 0,
            trend: data.comparison.all?.trend ?? 'NO_CHANGE',
          }
          : null,
        close: data?.comparison?.close
          ? {
            current: data.comparison.close?.current ?? 0,
            previous: data.comparison.close?.previous ?? 0,
            difference: data.comparison.close?.difference ?? 0,
            trend: data.comparison.close?.trend ?? 'NO_CHANGE',
          }
          : null,
        progress: data?.comparison?.progress
          ? {
            current: data.comparison.progress?.current ?? 0,
            previous: data.comparison.progress?.previous ?? 0,
            difference: data.comparison.progress?.difference ?? 0,
            trend: data.comparison.progress?.trend ?? 'NO_CHANGE',
          }
          : null,
        notClose: data?.comparison?.notClose
          ? {
            current: data.comparison.notClose?.current ?? 0,
            previous: data.comparison.notClose?.previous ?? 0,
            difference: data.comparison.notClose?.difference ?? 0,
            trend: data.comparison.notClose?.trend ?? 'NO_CHANGE',
          }
          : null,
        metSla: data?.comparison?.metSla
          ? {
            current: data.comparison.metSla?.current ?? 0,
            previous: data.comparison.metSla?.previous ?? 0,
            difference: data.comparison.metSla?.difference ?? 0,
            trend: data.comparison.metSla?.trend ?? 'NO_CHANGE',
          }
          : null,
        slaMiss: data?.comparison?.slaMiss
          ? {
            current: data.comparison.slaMiss?.current ?? 0,
            previous: data.comparison.slaMiss?.previous ?? 0,
            difference: data.comparison.slaMiss?.difference ?? 0,
            trend: data.comparison.slaMiss?.trend ?? 'NO_CHANGE',
          }
          : null,
        type: data?.comparison?.type
          ? {
            current: data.comparison.type?.current ?? 0,
            previous: data.comparison.type?.previous ?? 0,
            difference: data.comparison.type?.difference ?? 0,
            trend: data.comparison.type?.trend ?? 'NO_CHANGE',
          }
          : null,
        successful: data?.comparison?.successful
          ? {
            current: data.comparison.successful?.current ?? 0,
            previous: data.comparison.successful?.previous ?? 0,
            difference: data.comparison.successful?.difference ?? 0,
            trend: data.comparison.successful?.trend ?? 'NO_CHANGE',
          }
          : null,
        unsuccessful: data?.comparison?.unsuccessful
          ? {
            current: data.comparison.unsuccessful?.current ?? 0,
            previous: data.comparison.unsuccessful?.previous ?? 0,
            difference: data.comparison.unsuccessful?.difference ?? 0,
            trend: data.comparison.unsuccessful?.trend ?? 'NO_CHANGE',
          }
          : null,
      };

      setChangeSummary(transformedData);
      setSummary((prev: any) => ({
        ...prev,
        change: aggregateSummary(transformedData, ["all"]),
      }));
    } catch (error) {
      console.error('Error loading summary:', error);
    }
  };


  const loadAlerts = async (dateParams?: DateParams) => {
    const isListView = !groupBy || groupBy === 'none';
    const newFilters = getFiltersForTile(liveTile, appliedFilters, activeParentTile);
    const dynamicFilters = buildFilters({...appliedFilters, ...newFilters });

    const alertPageSignature = JSON.stringify({
      pageSize,
      liveTile,
      sortingInfo,
      dynamicFilters,
      searchText,
      columnFilters: columnFiltersRef.current,
      dateRangeType: sessionStorage.getItem('unifiedAlertsDateRangeType'),
    });

    const alertPageRequest = isListView
      ? getAlertPageRequest(alertPageCursorRef.current, alertPageSignature, page)
      : null;

    if (isListView && !alertPageRequest) {
      setPage(1);
      return;
    }

    const requestId = ++alertsListRequestIdRef.current;
    setIsLoading(true);

    try {
      const hasFilters = (obj: object) => Object.keys(obj).length > 0;
      const currentSearchAfter = latestStateRef.current.searchAfter;
      let searchNext = null;

      if (groupBy && groupBy !== 'none' && page > 1) {
        const afterKey = currentSearchAfter;
        if (afterKey && typeof afterKey === 'object' && Object.keys(afterKey).length > 0) {
          searchNext = afterKey;
        }
      }

      const params: any = {
        page,
        pageSize,
        search: searchText || undefined,
        severity: liveTile,
        ...getDatePayloadParams(dateParams),
        ...(hasFilters(dynamicFilters) && {
          filter: dynamicFilters,
        }),

        ...getItsmSortParams(sortingInfo),

        ...(searchNext && { searchNext }),
        ...(groupBy && groupBy !== 'none' && { groupBy }),
        ...(columnFiltersRef.current.length > 0 && { columnFilters: columnFiltersRef.current }),
        ...alertPageRequest,
      };

      const updatedParams = expandSectorFilter(params, filterOptions);

      const domainInput = updatedParams?.filter?.domain;
      const programInput = updatedParams?.filter?.program;

      const transformedParams = {
        ...updatedParams,
        parentTile: getParentTileValue(activeParentTile),
        filter: {
          ...updatedParams.filter,

          ...(domainInput
            ? {
              domain: expandMappedValues(domainInput, DOMAIN_MAP),
            }
            : {}),

          ...(programInput
            ? {
              program: expandMappedValues(programInput, PROGRAM_MAP),
            }
            : {}),
        },
      };
      const { assignmentGroupListName, ...cleanFilter } = transformedParams.filter;

      const cleanedTransformedParams = {
        ...transformedParams,
        filter: cleanFilter
      };
      const response = await unifiedAlertsService.getITSMData(cleanedTransformedParams, activeParentTile);
      if (requestId !== alertsListRequestIdRef.current) return;

      const responseData = response?.data;
      const groupedResponse = response?.isGrouped === true || responseData?.isGrouped === true;

      if (groupedResponse) {
        const groups = responseData?.data?.groups || responseData?.groups || response?.groups || [];
        const normalizedGroups = normalizeGroupedRows(groups, groupBy);
        setData(normalizedGroups);
        setTotalCount(
          responseData?.totalGroups ??
          responseData?.pagination?.totalGroups ??
          responseData?.pagination?.total ??
          response?.pagination?.totalGroups ??
          response?.pagination?.total ??
          normalizedGroups.length,
        );
        setSearchAfter(responseData?.searchNext || response?.searchNext || null);
        alertPageCursorRef.current = null;
        setIsGrouped(true);
        setIsDataOpsAdmin(response?.isDataOpsAdmin);
      } else {
        for(let row of response.data) {
          if(!row.cmdb_ci) {
            row.cmdb_ci = 'CI not available';
          }
          if(!row.service_offering){
            row.service_offering = 'SO not available';
          }
        }
        setData(response.data ?? []);
        setTotalCount(response?.pagination?.total ?? responseData?.pagination?.total ?? responseData?.total ?? response?.data?.length ?? 0);
        setSearchAfter(response?.searchNext || responseData?.searchNext || null);
        const pagination = response?.pagination ?? responseData?.pagination;
        alertPageCursorRef.current = alertPageRequest && {
          signature: alertPageSignature,
          page,
          firstSortValues: pagination?.firstSortValues ?? null,
          lastSortValues: pagination?.lastSortValues ?? null,
          request: alertPageRequest,
        };
        setIsGrouped(false);
        setIsDataOpsAdmin(response?.isDataOpsAdmin);
      }
    } catch (error) {
      if (requestId !== alertsListRequestIdRef.current) return;
      console.error('Error loading incidents:', error);
    } finally {
      if (requestId === alertsListRequestIdRef.current) {
        setIsLoading(false);
      }
    }
  };

  const loadDropDownData = async (dateParams?: DateParams) => {
    try {
      const params: any = {
        liveTile,
      };

      const domainInput = params?.filter?.domain;
      const programInput = params?.filter?.program;

      const transformedParams = {
        ...params,
        parentTile: getParentTileValue(activeParentTile),
        filter: {
          ...params.filter,

          ...(domainInput
            ? {
              domain: expandMappedValues(domainInput, DOMAIN_MAP),
            }
            : {}),

          ...(programInput
            ? {
              program: expandMappedValues(programInput, PROGRAM_MAP),
            }
            : {}),
        },
      };

      const { assignmentGroupListName, ...cleanFilter } = transformedParams.filter;

      const cleanedTransformedParams = {
        ...transformedParams,
        filter: cleanFilter
      };

      const resp = await unifiedAlertsService.getIncidentDropDownData(cleanedTransformedParams);
      const { fullMap, ...response } = resp;
      const miscellaneousSectors = [
        "Russia",
        "AMESA APAC",
        "CTO",
        "DPS",
        "EXCASA",
        "GBS",
        "PBF",
        "PMF",
        "Quaker",
        "RnD",
        "SnT",
        "Soda Stream"
      ];
      response.sector = response.sector.filter(
        (sector: string) => !miscellaneousSectors.includes(sector)
      );

      response.sector.push('Miscellaneous');

      response.sectorDropdownValues = {
        ...response.sectorDropdownValues,
        Miscellaneous: miscellaneousSectors
      };

       const relationshipRecords = Array.from(
        new Map(
          Object.values(fullMap || {})
            .flatMap((fieldMap: any) =>
              Object.values(fieldMap || {})
                .flatMap((records: any) => records || [])
            )
            .map((record: any) => [
              JSON.stringify(record),
              record
            ])
        ).values()
      );
      setFilterRelationshipRecords(relationshipRecords || []);

      setOriginalFilterOptions({ ...DEFAULT_FILTER_OPTIONS, ...response });
      setFilterOptions({ ...DEFAULT_FILTER_OPTIONS, ...response });
    } catch (error) {
      console.error('Error loading incidents:', error);
    }
  };

  const loadAll = (dateParams?: DateParams) => {
    setFixedStartDate(dateParams?.startDate);
    setFixedEndDate(dateParams?.endDate);
    loadTrendData(dateParams);
    loadMostImpactedApps(dateParams);
    loadPriority(dateParams);
    loadProblems(dateParams);
    loadServiceSummary(dateParams);
    loadChangeSummary(dateParams);
  };

  loadAlertsRef.current = loadAlerts;
  loadAllRef.current = loadAll;

  const loadSummaries = (dateParams?: DateParams) => {
    setFixedStartDate(dateParams?.startDate);
    setFixedEndDate(dateParams?.endDate);
    loadPriority(dateParams);
    loadProblems(dateParams);
    loadServiceSummary(dateParams);
    loadChangeSummary(dateParams);
  };

  const loadActiveSectionSummary = () => {
    const dateParams: DateParams = fixedStartDate || fixedEndDate
      ? { startDate: fixedStartDate, endDate: fixedEndDate }
      : getDateParams();

    switch (activeParentTile) {
      case 'Incidents':
        loadPriority(dateParams);
        break;
      case 'Service Requests':
        loadServiceSummary(dateParams);
        break;
      case 'Problem Tickets':
        loadProblems(dateParams);
        break;
      case 'Change Requests':
        loadChangeSummary(dateParams);
        break;
      default:
        break;
    }
  };

  const handlePageNavigation = (newPage: number) => {
    if (newPage === page) return;
    isPageNavigationRef.current = true;
    setPage(newPage);
  };

  useEffect(() => {
    const handleStorageChange = () => {
      const saved = sessionStorage.getItem('unifiedAlertsAutoRefresh');
      if (saved !== null) setAutoRefreshInterval(saved === '0' ? null : Number(saved));
    };

    window.addEventListener('storage', handleStorageChange);
    const checkInterval = setInterval(() => {
      const saved = sessionStorage.getItem('unifiedAlertsAutoRefresh');
      const current = autoRefreshInterval === null ? 0 : autoRefreshInterval;
      if (saved !== null && Number(saved) !== current) {
        setAutoRefreshInterval(saved === '0' ? null : Number(saved));
      }
    }, 500);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(checkInterval);
    };
  }, [autoRefreshInterval]);

  useEffect(() => {
    const handleIncidentsSearch = (event: any) => {
      const searchText = event.detail?.searchText || '';
      setSimpleSearchText(searchText);
      setSearchText(searchText);
      setPage(1);
    };

    window.addEventListener('incidentsSearch', handleIncidentsSearch);
    return () => window.removeEventListener('incidentsSearch', handleIncidentsSearch);
  }, []);

  useEffect(() => {
    loadDropDownData();
  }, []);

  useEffect(() => {
    if (!isFiltersHydrated) return;
    if(ITSM_TILES_CONFIG.map(tile => tile.key).includes(liveTile)){
      loadTrendData()
      loadMostImpactedApps()
    }
  }, [liveTile]);

  useEffect(() => {
    if (!isFiltersHydrated) return;

    const tileKey = `${liveTile}|${activeTile}`;
    const isTileChange = lastLoadedTileRef.current !== tileKey;
    lastLoadedTileRef.current = tileKey;

    const isPageNavigation = isPageNavigationRef.current;
    isPageNavigationRef.current = false;

    if (isTileChange && isFiltersHydrated) {
      const dateParams = getDateParams();
      loadSummaries(dateParams);
      loadAlerts(dateParams);
      return;
    }
    if (isPageNavigation && isFiltersHydrated) {
      loadActiveSectionSummary();
    }
    loadAlerts();
  }, [page, pageSize, activeTile, liveTile, sortingInfo, groupBy, filterOptions, columnFilters]);

  useEffect(() => {
    setPage(1);
    setSearchAfter(null);
  }, [groupBy]);

  const loadSavedFiltersByParentTile = async (parentTile: string, applySavedTileMeta = false) => {
    const dashboardConfig = getDashboardFilterConfig(parentTile);

    const savedFilters = await loadDashboardFilters({
      dashboardId: dashboardConfig.dashboardId,
      dashboardName: dashboardConfig.dashboardName,
      pageName: dashboardConfig.pageName,
    });

    if (savedFilters?.filters) {
      const { savedActiveTile, savedLiveTile, saveActiveSubTitle, saveActiveParentTile } =
        savedFilters?.meta_data || {};

      const hydratedFilters = savedFilters.filters as FilterStateOps;

      setAppliedFilters(hydratedFilters);

      if (applySavedTileMeta) {
        setLiveTile(savedLiveTile || parentTile);
        setActiveParentTile(saveActiveParentTile || parentTile);
        setActiveSubTile(saveActiveSubTitle || parentTile);
        setActiveTile(savedActiveTile || (parentTile === 'Incidents' ? 'P3' : parentTile));
      } else {
        setLiveTile(savedLiveTile || parentTile);
        setActiveSubTile(saveActiveSubTitle || parentTile);
        setActiveTile(savedActiveTile || (parentTile === 'Incidents' ? 'P3' : parentTile));
      }

      persistDateRangeToSession(hydratedFilters);

      window.dispatchEvent(new Event('unifiedAlertsDateRangeHydrated'));
    } else if (applySavedTileMeta) {
      sessionStorage.setItem('unifiedAlertsDateRangeType', 'last_15_minutes');
      window.dispatchEvent(new Event('unifiedAlertsDateRangeHydrated'));
      setAppliedFilters(DEFAULT_FILTERS);

      setLiveTile(parentTile);
      setActiveParentTile(parentTile);
      setActiveSubTile(parentTile);
      setActiveTile(parentTile === 'Incidents' ? 'P3' : parentTile);
    } else {
      setLiveTile(parentTile);
      setActiveSubTile(parentTile);
      setActiveTile(parentTile === 'Incidents' ? 'P3' : parentTile);
    }
  };

  useEffect(() => {
    const loadSavedFilters = async () => {
      try {
        await loadSavedFiltersByParentTile(activeParentTile, true);
      } catch (e) {
        console.error('Failed to load saved filters', e);
      } finally {
        setIsFiltersHydrated(true);
      }
    };

    loadSavedFilters();
  }, []);

  useEffect(() => {
    if (!isFiltersHydrated) return;

    const loadSavedFilters = async () => {
      try {
        await loadSavedFiltersByParentTile(activeParentTile);
      } catch (e) {
        console.error('Failed to load saved filters', e);
      }
    };

    loadSavedFilters();
  }, [activeParentTile, isFiltersHydrated]);

  useEffect(() => {
    if (!isFiltersHydrated) return;

    loadAll(getDateParams());
    loadAlerts(getDateParams());
  }, [isFiltersHydrated]);

  useEffect(() => {
    if (!isFiltersHydrated) return;

    loadAll(getDateParams());
    loadAlerts(getDateParams());
  }, [appliedFilters, searchText, groupBy, filterOptions]);

  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (autoRefreshInterval) {
      intervalRef.current = setInterval(() => {
        loadAllRef.current?.(getDateParams());
        loadAlertsRef.current?.(getDateParams());
      }, autoRefreshInterval * 60 * 1000);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [autoRefreshInterval]);

  const handleTileClick = (key: string) => {
    setActiveTile(key);
    setPage(1);
    setSearchAfter(null);
    if (alertPageCursorRef) {
      alertPageCursorRef.current = null;
    }
  };

  const handleApplyFilters = (filters: FilterStateOps) => {
    const filtersToApply = withCustomDates(filters);
    setAppliedFilters(filtersToApply);
    persistDateRangeToSession(filtersToApply);
    setPage(1);
  };

  const handleSaveDefaultFilters = async (filters: FilterStateOps) => {
    const filtersToSave = withCustomDates(filters);

    setAppliedFilters(filtersToSave);
    persistDateRangeToSession(filtersToSave);

    const dashboardConfig = getDashboardFilterConfig(activeParentTile);

    await saveDashboardFilters(
      {
        dashboardId: dashboardConfig.dashboardId,
        dashboardName: dashboardConfig.dashboardName,
        pageName: dashboardConfig.pageName,
        meta_data: {
          saveActiveParentTile: activeParentTile,
          saveActiveSubTitle: activeSubTile,
          savedActiveTile: activeTile,
          savedLiveTile: liveTile,
        },
      },
      filtersToSave,
    ).catch(() => {});

    setPage(1);
    setSearchAfter(null);
    if (alertPageCursorRef) {
      alertPageCursorRef.current = null;
    }
  };

  const handleResetDefaultFilters = async (filters: FilterStateOps) => {
    const filtersToReset = withCustomDates(filters);

    setAppliedFilters(filtersToReset);
    persistDateRangeToSession(filtersToReset);

    const resetLiveTile = activeParentTile;
    const resetSubTile = activeParentTile;
    const resetActiveTile = activeParentTile === 'Incidents' ? 'P3' : activeParentTile;

    setLiveTile(resetLiveTile);
    setActiveParentTile(activeParentTile);
    setActiveSubTile(resetSubTile);
    setActiveTile(resetActiveTile);

    const dashboardConfig = getDashboardFilterConfig(activeParentTile);

    await saveDashboardFilters(
      {
        dashboardId: dashboardConfig.dashboardId,
        dashboardName: dashboardConfig.dashboardName,
        pageName: dashboardConfig.pageName,
        meta_data: {
          saveActiveParentTile: activeParentTile,
          saveActiveSubTitle: resetSubTile,
          savedActiveTile: resetActiveTile,
          savedLiveTile: resetLiveTile,
        },
      },
      filtersToReset,
    ).catch(() => {});

    setPage(1);
    setSearchAfter(null);
    if (alertPageCursorRef) {
      alertPageCursorRef.current = null;
    }
  };

  const handleExport = async () => {
    if (isExporting) return;
    setIsExporting(true);
    const minDelay = new Promise((resolve) => setTimeout(resolve, 2000));
    const newFilters = getFiltersForTile(liveTile, appliedFilters, activeParentTile);
    const dynamicFilters = buildFilters({...appliedFilters, ...newFilters });
    const hasFilters = (obj: object) => Object.keys(obj).length > 0;
    try {


      const params: any = {
        page: 1,
        pageSize: 200000,
        search: searchText || undefined,
        severity: liveTile,
        ...getDatePayloadParams(getDateParams()),
        ...(hasFilters(dynamicFilters) && {
          filter: dynamicFilters,
        }),

        ...getItsmSortParams(sortingInfo),

        ...(groupBy && groupBy !== 'none' && { groupBy }),
        ...(columnFiltersRef.current.length > 0 && { columnFilters: columnFiltersRef.current }),
      };

      const updatedParams = expandSectorFilter(params, filterOptions);

      const domainInput = updatedParams?.filter?.domain;
      const programInput = updatedParams?.filter?.program;

      const transformedParams = {
        ...updatedParams,
        parentTile: getParentTileValue(activeParentTile),
        filter: {
          ...updatedParams.filter,

          ...(domainInput
            ? {
              domain: expandMappedValues(domainInput, DOMAIN_MAP),
            }
            : {}),

          ...(programInput
            ? {
              program: expandMappedValues(programInput, PROGRAM_MAP),
            }
            : {}),
        },
      };

      const { assignmentGroupListName, ...cleanFilter } = transformedParams.filter;

      const cleanedTransformedParams = {
        ...transformedParams,
        filter: cleanFilter
      };

      await Promise.all([
        unifiedAlertsService.exportITSMAlerts(cleanedTransformedParams, activeParentTile),
        minDelay,
      ]);
    } catch (error) {
      console.error('Error exporting alerts:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const fetchPdfData = async (): Promise<any[]> => {

    const newFilters = getFiltersForTile(liveTile, appliedFilters, activeParentTile);
    const dynamicFilters = buildFilters({...appliedFilters, ...newFilters });
    const hasFilters = (obj: object) => Object.keys(obj).length > 0;
    try {
     const params: any = {
        page: 1,
        pageSize: 500,
        search: searchText || undefined,
        severity: liveTile,
        ...getDatePayloadParams(getDateParams()),
        ...(hasFilters(dynamicFilters) && {
          filter: dynamicFilters,
        }),

        ...getItsmSortParams(sortingInfo),

        ...(groupBy && groupBy !== 'none' && { groupBy }),
        ...(columnFiltersRef.current.length > 0 && { columnFilters: columnFiltersRef.current }),
      };

      const updatedParams = expandSectorFilter(params, filterOptions);

      const domainInput = updatedParams?.filter?.domain;
      const programInput = updatedParams?.filter?.program;

      const transformedParams = {
        ...updatedParams,
        parentTile: getParentTileValue(activeParentTile),
        filter: {
          ...updatedParams.filter,

          ...(domainInput
            ? {
              domain: expandMappedValues(domainInput, DOMAIN_MAP),
            }
            : {}),

          ...(programInput
            ? {
              program: expandMappedValues(programInput, PROGRAM_MAP),
            }
            : {}),
        },
      };
      const { assignmentGroupListName, ...cleanFilter } = transformedParams.filter;

      const cleanedTransformedParams = {
        ...transformedParams,
        filter: cleanFilter
      };


      const response = await unifiedAlertsService.getITSMData(cleanedTransformedParams, activeParentTile);
      return response.data ?? [];
    } catch (error) {
      console.error('Error fetching PDF data:', error);
      return [];
    }
  };

  const handleColumnFilterChange = useCallback((payload: ColumnFilterPayloadItem[], sortModel: SortModel) => {
    saveColumnFilterPayload(columnFilterStorageKey, payload);
    setColumnFilterVersion((version) => version + 1);
    setSortingInfo(getSortingFromSortModel(sortModel, DEFAULT_ITSM_SORTING));
    setPage(1);
  }, [columnFilterStorageKey]);


  const fetchColumnFilterOptions = async (): Promise<any[] | null> => {
    // /dropdown-data has no status values, so STATUS uses the ticket type's status list (the same list as the filter drawer)
    const statusKey = activeParentTile === 'Incidents' ? 'incident_state' : 'state';

    const fixedOptions = [
      {
        id: statusKey,
        values: DEFAULT_FILTER_OPTIONS[STATUS_OPTION_KEY_BY_TYPE[getParentTileValue(activeParentTile)]] ?? [],
      },
      { id: 'made_sla', values: ['true', 'false'] },
      { id: 'isDandAIAG', values: DEFAULT_FILTER_OPTIONS.isDandAIAG },

      { id: 'urgency', values: DEFAULT_FILTER_OPTIONS.urgency },
   
      { id: 'contact_type', values: DEFAULT_FILTER_OPTIONS.contact_type },
    ];
    const fixedOptionIds = fixedOptions.map((option) => option.id);

    // The shared filter popup does not handle rejections, so a failed request must resolve to a list here
    try {
      const response = await unifiedAlertsService.getIncidentDropDownData({
        liveTile: activeParentTile,
        parentTile: getParentTileValue(activeParentTile),
      });
      const responseOptions = toColumnFilterOptions(response) ?? [];
      // Values taken from the tickets come back under column_* keys and replace the drawer's lists for these columns
      const ticketOptions = responseOptions
        .filter((option) => TICKET_VALUE_KEYS[option?.id])
        .map((option) => ({ id: TICKET_VALUE_KEYS[option.id], values: option.values }));
      const options = [
        ...ticketOptions,
        ...responseOptions.filter(
          (option) => !TICKET_VALUE_KEYS[option?.id] && !ticketOptions.some((ticket) => ticket.id === option?.id)
        ),
      ]
        .filter((option) => !(fixedOptionIds.includes(option?.id) && !option.values?.length))
        // Empty CI / service offering are shown in the table as "CI not available" / "SO not available"
        .map((option) => (EMPTY_VALUE_LABELS[option?.id]
          ? {
            ...option,
            values: option.values.map((value: any) => (value === '' ? { label: EMPTY_VALUE_LABELS[option.id], value: '' } : value)),
          }
          : option));
      return [...options, ...fixedOptions.filter((fixed) => !options.some((option) => option?.id === fixed.id))];
    } catch (error) {
      console.error('Error loading column filter values:', error);
      return fixedOptions;
    }
  };

  useEffect(() => {
    columnFilterOptionsRef.current = fetchColumnFilterOptions();
  }, [activeParentTile]);

  const getColumnFilterValues = useCallback(async (field: string): Promise<any[]> => {
    if (field === 'isDandAIAG') return ['Yes', 'No'];
    const response = (await columnFilterOptionsRef.current) ?? (await fetchColumnFilterOptions());
    const values = response?.find((option: { id: string }) => option?.id === field)?.values;
    return Array.isArray(values)
      ? values.filter(
          (v: any) =>
            v !== null &&
            v !== undefined &&
            String(v).trim() !== '' &&
            String(v).trim().toLowerCase() !== '(blank)'
        )
      : [];
  }, [activeParentTile]);

  return {
    activeTile,
    page,
    pageSize,
    searchText,
    simpleSearchText,
    sortingInfo,
    appliedFilters,
    summary,
    incidentSummary,
    problemSummary,
    serviceSummary,
    changeSummary,
    data,
    totalCount,
    isLoading,
    isExporting,
    filterOptions,
    setFilterOptions,
    originalfilterOptions,
    fixedStartDate,
    fixedEndDate,
    trendSector,
    trendProgram,
    mostImpactedApps,
    isChartsLoading,
    isFiltersHydrated,
    setFixedStartDate,
    setFixedEndDate,
    setPage,
    handlePageNavigation,
    setPageSize,
    setSearchText,
    setSimpleSearchText,
    setSortingInfo,
    handleTileClick,
    handleApplyFilters,
    handleSaveDefaultFilters,
    handleResetDefaultFilters,
    handleExport,
    liveTile,
    setLiveTile,
    activeParentTile,
    setActiveParentTile,
    setActiveSubTile,
    activeSubTile,
    setGroupBy,
    groupBy,
    isGrouped,
    setIsGrouped,
    loadMoreChildren,
    isLoadingMoreChildren,
    loadingGroupId,
    setLoadingGroupId,
    fetchPdfData,
    isDataOpsAdmin,
    loadAlerts,
    getDateParams,
    expandMappedValues,
    filterRelationshipRecords,
    columnFilters,
    handleColumnFilterChange,
    getColumnFilterValues
  };
};
